import moment from 'moment';
import api from '@/common/api';
import { utcToLocalDate } from '@/common/calendarTime';

// Модель бэкенда — три независимые сущности (Availability/Appointment/Expert+услуги),
// см. audit/calendar-api-reference.md. Свобода времени определяется Availability.status
// === 'open', брони — отдельная сущность appointments со своим статусом/переходами.

const normalizeList = (response) => {
  if (Array.isArray(response)) return response;
  if (response && Array.isArray(response.results)) return response.results;
  return [];
};

const initialState = () => ({
  loading: {
    availability: false,
    appointments: false,
    experts: false,
    serviceTypes: false,
    services: false,
    action: false,
  },
  errors: {
    availability: [],
    appointments: [],
    experts: [],
    serviceTypes: [],
    services: [],
    action: [],
  },

  availability: [],
  appointments: [],
  experts: [],
  serviceTypes: [],
  services: [],

  currentMonth: moment().format('YYYY-MM'),
  selectedDate: moment().format('YYYY-MM-DD'),

  availabilityDialog: {
    show: false,
    id: null,
  },

  loadingCalendarData: false,
  notification: {
    show: false,
    success: true,
    message: '',
  },
});

export default {
  namespaced: true,
  state: initialState,
  actions: {
    async loadCalendarData({ dispatch, commit }) {
      commit('SET_LOADING_CALENDAR_DATA', true);
      try {
        await Promise.all([
          dispatch('loadAvailability'),
          dispatch('loadAppointments'),
          dispatch('loadExperts'),
          dispatch('loadServiceTypes'),
          dispatch('loadServices'),
        ]);
      } finally {
        commit('SET_LOADING_CALENDAR_DATA', false);
      }
    },

    // ---------- Availability ----------
    async loadAvailability({ commit }) {
      commit('SET_LOADING', { key: 'availability', value: true });
      commit('SET_ERRORS', { key: 'availability', value: [] });
      try {
        const response = await api.get('/calendar/manage/availability/');
        commit('SET_LIST', { key: 'availability', items: normalizeList(response) });
      } catch (err) {
        commit('SET_ERRORS', { key: 'availability', value: Object.entries(err.data || {}) });
        throw err;
      } finally {
        commit('SET_LOADING', { key: 'availability', value: false });
      }
    },

    async createAvailability({ commit, dispatch }, {
      expert, starts_at: startsAt, ends_at: endsAt, unavailable_services: unavailableServices,
    }) {
      commit('SET_LOADING', { key: 'action', value: true });
      try {
        const body = { expert, starts_at: startsAt, ends_at: endsAt };
        // unavailable_services — id услуг эксперта, исключённых из этого интервала.
        // null/[] снимают все исключения; отсутствие ключа на бэке = «не менять».
        if (unavailableServices !== undefined) body.unavailable_services = unavailableServices;
        await api.post('/calendar/manage/availability/', body);
        await dispatch('loadAvailability');
        commit('SET_NOTIFICATION', { show: true, success: true, message: 'Интервал доступности добавлен' });
      } catch (err) {
        commit('SET_ERRORS', { key: 'availability', value: Object.entries(err.data || {}) });
        commit('SET_NOTIFICATION', { show: true, success: false, message: 'Не удалось добавить интервал. Попробуйте позже.' });
        throw err;
      } finally {
        commit('SET_LOADING', { key: 'action', value: false });
      }
    },

    async updateAvailability({ commit, dispatch }, {
      id, expert, starts_at: startsAt, ends_at: endsAt, unavailable_services: unavailableServices,
    }) {
      commit('SET_LOADING', { key: 'action', value: true });
      try {
        const body = { expert, starts_at: startsAt, ends_at: endsAt };
        if (unavailableServices !== undefined) body.unavailable_services = unavailableServices;
        await api.patch(`/calendar/manage/availability/${id}/`, body);
        await dispatch('loadAvailability');
        commit('SET_NOTIFICATION', { show: true, success: true, message: 'Интервал доступности обновлён' });
      } catch (err) {
        commit('SET_ERRORS', { key: 'availability', value: Object.entries(err.data || {}) });
        commit('SET_NOTIFICATION', { show: true, success: false, message: 'Не удалось обновить интервал. Попробуйте позже.' });
        throw err;
      } finally {
        commit('SET_LOADING', { key: 'action', value: false });
      }
    },

    // DELETE на бэке — это закрытие интервала (status -> closed), не жёсткое удаление.
    async closeAvailability({ commit, dispatch }, id) {
      commit('SET_LOADING', { key: 'action', value: true });
      try {
        await api.delete(`/calendar/manage/availability/${id}/`);
        await dispatch('loadAvailability');
        commit('SET_NOTIFICATION', { show: true, success: true, message: 'Интервал закрыт' });
      } catch (err) {
        commit('SET_ERRORS', { key: 'availability', value: Object.entries(err.data || {}) });
        commit('SET_NOTIFICATION', { show: true, success: false, message: 'Не удалось закрыть интервал. Попробуйте позже.' });
        throw err;
      } finally {
        commit('SET_LOADING', { key: 'action', value: false });
      }
    },

    // ---------- Appointments ----------
    async loadAppointments({ commit }) {
      commit('SET_LOADING', { key: 'appointments', value: true });
      commit('SET_ERRORS', { key: 'appointments', value: [] });
      try {
        const response = await api.get('/calendar/manage/appointments/');
        commit('SET_LIST', { key: 'appointments', items: normalizeList(response) });
      } catch (err) {
        commit('SET_ERRORS', { key: 'appointments', value: Object.entries(err.data || {}) });
        throw err;
      } finally {
        commit('SET_LOADING', { key: 'appointments', value: false });
      }
    },

    // 409 значит, что статус брони уже поменялся (принята/отклонена в другом месте) —
    // это не ошибка сервера, а рассинхронизация списка, поэтому просто перезагружаем список.
    async acceptAppointment({ commit, dispatch }, { id, conference_url: conferenceUrl }) {
      commit('SET_LOADING', { key: 'action', value: true });
      try {
        const body = conferenceUrl ? { conference_url: conferenceUrl } : {};
        await api.post(`/calendar/manage/appointments/${id}/accept/`, body);
        await dispatch('loadAppointments');
        commit('SET_NOTIFICATION', { show: true, success: true, message: 'Запись подтверждена' });
      } catch (err) {
        await dispatch('handleAppointmentActionError', err);
        throw err;
      } finally {
        commit('SET_LOADING', { key: 'action', value: false });
      }
    },

    async declineAppointment({ commit, dispatch }, id) {
      commit('SET_LOADING', { key: 'action', value: true });
      try {
        await api.post(`/calendar/manage/appointments/${id}/decline/`, {});
        await dispatch('loadAppointments');
        commit('SET_NOTIFICATION', { show: true, success: true, message: 'Запись отклонена' });
      } catch (err) {
        await dispatch('handleAppointmentActionError', err);
        throw err;
      } finally {
        commit('SET_LOADING', { key: 'action', value: false });
      }
    },

    async setConferenceLink({ commit, dispatch }, { id, conference_url: conferenceUrl }) {
      commit('SET_LOADING', { key: 'action', value: true });
      try {
        await api.patch(`/calendar/manage/appointments/${id}/conference-link/`, { conference_url: conferenceUrl });
        await dispatch('loadAppointments');
        commit('SET_NOTIFICATION', { show: true, success: true, message: 'Ссылка на конференцию сохранена' });
      } catch (err) {
        commit('SET_ERRORS', { key: 'appointments', value: Object.entries(err.data || {}) });
        commit('SET_NOTIFICATION', { show: true, success: false, message: 'Не удалось сохранить ссылку. Попробуйте позже.' });
        throw err;
      } finally {
        commit('SET_LOADING', { key: 'action', value: false });
      }
    },

    async handleAppointmentActionError({ commit, dispatch }, err) {
      if (err.status === 409) {
        commit('SET_NOTIFICATION', {
          show: true,
          success: false,
          message: 'Статус записи уже изменился, список обновлён',
        });
        await dispatch('loadAppointments');
        return;
      }
      commit('SET_ERRORS', { key: 'appointments', value: Object.entries(err.data || {}) });
      commit('SET_NOTIFICATION', { show: true, success: false, message: 'Не удалось выполнить действие. Попробуйте позже.' });
    },

    // ---------- Experts ----------
    async loadExperts({ commit }) {
      commit('SET_LOADING', { key: 'experts', value: true });
      commit('SET_ERRORS', { key: 'experts', value: [] });
      try {
        const response = await api.get('/calendar/manage/experts/');
        commit('SET_LIST', { key: 'experts', items: normalizeList(response) });
      } catch (err) {
        commit('SET_ERRORS', { key: 'experts', value: Object.entries(err.data || {}) });
        throw err;
      } finally {
        commit('SET_LOADING', { key: 'experts', value: false });
      }
    },

    async createExpert({ commit, dispatch }, { user, short_description: shortDescription, description }) {
      commit('SET_LOADING', { key: 'action', value: true });
      try {
        await api.post('/calendar/manage/experts/', { user, short_description: shortDescription, description });
        await dispatch('loadExperts');
        commit('SET_NOTIFICATION', { show: true, success: true, message: 'Эксперт добавлен' });
      } catch (err) {
        commit('SET_ERRORS', { key: 'experts', value: Object.entries(err.data || {}) });
        commit('SET_NOTIFICATION', { show: true, success: false, message: 'Не удалось добавить эксперта. Попробуйте позже.' });
        throw err;
      } finally {
        commit('SET_LOADING', { key: 'action', value: false });
      }
    },

    async updateExpert({ commit, dispatch }, {
      id, short_description: shortDescription, description, is_active: isActive,
    }) {
      commit('SET_LOADING', { key: 'action', value: true });
      try {
        await api.patch(`/calendar/manage/experts/${id}/`, {
          short_description: shortDescription, description, is_active: isActive,
        });
        await dispatch('loadExperts');
        commit('SET_NOTIFICATION', { show: true, success: true, message: 'Данные эксперта обновлены' });
      } catch (err) {
        commit('SET_ERRORS', { key: 'experts', value: Object.entries(err.data || {}) });
        commit('SET_NOTIFICATION', { show: true, success: false, message: 'Не удалось обновить эксперта. Попробуйте позже.' });
        throw err;
      } finally {
        commit('SET_LOADING', { key: 'action', value: false });
      }
    },

    async deleteExpert({ commit, dispatch }, id) {
      commit('SET_LOADING', { key: 'action', value: true });
      try {
        await api.delete(`/calendar/manage/experts/${id}/`);
        await dispatch('loadExperts');
        commit('SET_NOTIFICATION', { show: true, success: true, message: 'Эксперт удалён' });
      } catch (err) {
        commit('SET_ERRORS', { key: 'experts', value: Object.entries(err.data || {}) });
        commit('SET_NOTIFICATION', { show: true, success: false, message: 'Не удалось удалить эксперта. Попробуйте позже.' });
        throw err;
      } finally {
        commit('SET_LOADING', { key: 'action', value: false });
      }
    },

    // Поле имени файла в multipart не подтверждено бэком в справке — уточнить при интеграции.
    async uploadExpertPhoto({ commit, dispatch }, { id, file }) {
      commit('SET_LOADING', { key: 'action', value: true });
      try {
        const formData = new FormData();
        formData.append('file', file);
        await api.post(`/calendar/manage/experts/${id}/upload-photo/`, formData);
        await dispatch('loadExperts');
      } catch (err) {
        commit('SET_ERRORS', { key: 'experts', value: Object.entries(err.data || {}) });
        commit('SET_NOTIFICATION', { show: true, success: false, message: 'Не удалось загрузить фото. Попробуйте позже.' });
        throw err;
      } finally {
        commit('SET_LOADING', { key: 'action', value: false });
      }
    },

    async deleteExpertPhoto({ commit, dispatch }, id) {
      commit('SET_LOADING', { key: 'action', value: true });
      try {
        await api.delete(`/calendar/manage/experts/${id}/delete-photo/`);
        await dispatch('loadExperts');
      } catch (err) {
        commit('SET_NOTIFICATION', { show: true, success: false, message: 'Не удалось удалить фото. Попробуйте позже.' });
        throw err;
      } finally {
        commit('SET_LOADING', { key: 'action', value: false });
      }
    },

    // Эксперт и его услуги — разные сущности на бэке без общего bulk-эндпоинта, поэтому
    // сохраняем сначала эксперта (нужен id для новой записи), потом его услуги.
    async saveExpertWithServices({
      commit, dispatch, getters,
    }, { expert, services }) {
      commit('SET_LOADING', { key: 'action', value: true });
      try {
        let expertId = expert.id;
        if (expertId) {
          await api.patch(`/calendar/manage/experts/${expertId}/`, {
            short_description: expert.short_description,
            description: expert.description,
            is_active: expert.is_active,
          });
        } else {
          const created = await api.post('/calendar/manage/experts/', {
            user: expert.user,
            short_description: expert.short_description,
            description: expert.description,
          });
          expertId = created.id;
        }

        const existing = getters.servicesByExpert(expertId);
        const tasks = (services || []).map((row) => {
          if (!row.id) {
            return api.post('/calendar/manage/services/', {
              expert: expertId,
              service_type: row.service_type,
              max_duration_minutes: row.max_duration_minutes,
            });
          }
          const current = existing.find((item) => item.id === row.id);
          const currentIsActive = current ? current.is_active !== false : true;
          const changed = !current
            || current.service_type !== row.service_type
            || current.max_duration_minutes !== row.max_duration_minutes
            || currentIsActive !== row.is_active;
          if (!changed) return null;
          return api.put(`/calendar/manage/services/${row.id}/`, {
            expert: expertId,
            service_type: row.service_type,
            max_duration_minutes: row.max_duration_minutes,
            is_active: row.is_active,
          });
        }).filter(Boolean);

        await Promise.all(tasks);
        await Promise.all([dispatch('loadExperts'), dispatch('loadServices')]);
        commit('SET_NOTIFICATION', {
          show: true,
          success: true,
          message: expert.id ? 'Данные эксперта обновлены' : 'Эксперт добавлен',
        });
      } catch (err) {
        commit('SET_ERRORS', { key: 'experts', value: Object.entries(err.data || {}) });
        commit('SET_NOTIFICATION', { show: true, success: false, message: 'Не удалось сохранить эксперта. Попробуйте позже.' });
        throw err;
      } finally {
        commit('SET_LOADING', { key: 'action', value: false });
      }
    },

    // ---------- Service types (справочник) ----------
    async loadServiceTypes({ commit }) {
      commit('SET_LOADING', { key: 'serviceTypes', value: true });
      commit('SET_ERRORS', { key: 'serviceTypes', value: [] });
      try {
        const response = await api.get('/calendar/manage/service-types/');
        commit('SET_LIST', { key: 'serviceTypes', items: normalizeList(response) });
      } catch (err) {
        commit('SET_ERRORS', { key: 'serviceTypes', value: Object.entries(err.data || {}) });
        throw err;
      } finally {
        commit('SET_LOADING', { key: 'serviceTypes', value: false });
      }
    },

    async createServiceType({ commit, dispatch }, { code, name }) {
      commit('SET_LOADING', { key: 'action', value: true });
      try {
        await api.post('/calendar/manage/service-types/', { code, name });
        await dispatch('loadServiceTypes');
        commit('SET_NOTIFICATION', { show: true, success: true, message: 'Тип услуги добавлен' });
      } catch (err) {
        commit('SET_ERRORS', { key: 'serviceTypes', value: Object.entries(err.data || {}) });
        commit('SET_NOTIFICATION', { show: true, success: false, message: 'Не удалось добавить тип услуги. Попробуйте позже.' });
        throw err;
      } finally {
        commit('SET_LOADING', { key: 'action', value: false });
      }
    },

    async updateServiceType({ commit, dispatch }, { id, code, name }) {
      commit('SET_LOADING', { key: 'action', value: true });
      try {
        await api.patch(`/calendar/manage/service-types/${id}/`, { code, name });
        await dispatch('loadServiceTypes');
        commit('SET_NOTIFICATION', { show: true, success: true, message: 'Тип услуги обновлён' });
      } catch (err) {
        commit('SET_ERRORS', { key: 'serviceTypes', value: Object.entries(err.data || {}) });
        commit('SET_NOTIFICATION', { show: true, success: false, message: 'Не удалось обновить тип услуги. Попробуйте позже.' });
        throw err;
      } finally {
        commit('SET_LOADING', { key: 'action', value: false });
      }
    },

    // Удаления у ServiceType на бэке нет — деактивация через is_active (PUT, подтверждено бэком).
    async setServiceTypeActive({ commit, dispatch, getters }, { id, is_active: isActive }) {
      commit('SET_LOADING', { key: 'action', value: true });
      try {
        const current = getters.serviceTypeById(id) || {};
        await api.put(`/calendar/manage/service-types/${id}/`, {
          code: current.code, name: current.name, is_active: isActive,
        });
        await dispatch('loadServiceTypes');
        commit('SET_NOTIFICATION', {
          show: true,
          success: true,
          message: isActive ? 'Тип услуги включён' : 'Тип услуги отключён',
        });
      } catch (err) {
        commit('SET_ERRORS', { key: 'serviceTypes', value: Object.entries(err.data || {}) });
        commit('SET_NOTIFICATION', { show: true, success: false, message: 'Не удалось изменить статус типа услуги. Попробуйте позже.' });
        throw err;
      } finally {
        commit('SET_LOADING', { key: 'action', value: false });
      }
    },

    // ---------- Bookable services (эксперт + тип услуги + длительность) ----------
    async loadServices({ commit }) {
      commit('SET_LOADING', { key: 'services', value: true });
      commit('SET_ERRORS', { key: 'services', value: [] });
      try {
        const response = await api.get('/calendar/manage/services/');
        commit('SET_LIST', { key: 'services', items: normalizeList(response) });
      } catch (err) {
        commit('SET_ERRORS', { key: 'services', value: Object.entries(err.data || {}) });
        throw err;
      } finally {
        commit('SET_LOADING', { key: 'services', value: false });
      }
    },

    async createService({ commit, dispatch }, {
      expert, service_type: serviceType, max_duration_minutes: maxDurationMinutes,
    }) {
      commit('SET_LOADING', { key: 'action', value: true });
      try {
        await api.post('/calendar/manage/services/', {
          expert, service_type: serviceType, max_duration_minutes: maxDurationMinutes,
        });
        await dispatch('loadServices');
        commit('SET_NOTIFICATION', { show: true, success: true, message: 'Услуга эксперта добавлена' });
      } catch (err) {
        commit('SET_ERRORS', { key: 'services', value: Object.entries(err.data || {}) });
        commit('SET_NOTIFICATION', { show: true, success: false, message: 'Не удалось добавить услугу. Попробуйте позже.' });
        throw err;
      } finally {
        commit('SET_LOADING', { key: 'action', value: false });
      }
    },

    async updateService({ commit, dispatch }, {
      id, expert, service_type: serviceType, max_duration_minutes: maxDurationMinutes,
    }) {
      commit('SET_LOADING', { key: 'action', value: true });
      try {
        await api.patch(`/calendar/manage/services/${id}/`, {
          expert, service_type: serviceType, max_duration_minutes: maxDurationMinutes,
        });
        await dispatch('loadServices');
        commit('SET_NOTIFICATION', { show: true, success: true, message: 'Услуга эксперта обновлена' });
      } catch (err) {
        commit('SET_ERRORS', { key: 'services', value: Object.entries(err.data || {}) });
        commit('SET_NOTIFICATION', { show: true, success: false, message: 'Не удалось обновить услугу. Попробуйте позже.' });
        throw err;
      } finally {
        commit('SET_LOADING', { key: 'action', value: false });
      }
    },

    // Удаления у BookableService на бэке нет — деактивация через is_active (PUT, подтверждено бэком).
    async setServiceActive({ commit, dispatch, getters }, { id, is_active: isActive }) {
      commit('SET_LOADING', { key: 'action', value: true });
      try {
        const current = getters.serviceById(id) || {};
        await api.put(`/calendar/manage/services/${id}/`, {
          expert: current.expert,
          service_type: current.service_type,
          max_duration_minutes: current.max_duration_minutes,
          is_active: isActive,
        });
        await dispatch('loadServices');
        commit('SET_NOTIFICATION', {
          show: true,
          success: true,
          message: isActive ? 'Услуга включена' : 'Услуга отключена',
        });
      } catch (err) {
        commit('SET_ERRORS', { key: 'services', value: Object.entries(err.data || {}) });
        commit('SET_NOTIFICATION', { show: true, success: false, message: 'Не удалось изменить статус услуги. Попробуйте позже.' });
        throw err;
      } finally {
        commit('SET_LOADING', { key: 'action', value: false });
      }
    },

    // ---------- UI state ----------
    setSelectedDate({ commit }, date) {
      commit('SET_SELECTED_DATE', date);
    },

    setCurrentMonth({ commit }, month) {
      commit('SET_CURRENT_MONTH', month);
    },

    openCreateAvailability({ commit }) {
      commit('SET_AVAILABILITY_DIALOG', { show: true, id: null });
    },

    openEditAvailability({ commit }, id) {
      commit('SET_AVAILABILITY_DIALOG', { show: true, id });
    },

    closeAvailabilityDialog({ commit }) {
      commit('SET_AVAILABILITY_DIALOG', { show: false, id: null });
    },
  },
  mutations: {
    SET_LOADING(state, { key, value }) {
      state.loading[key] = value;
    },
    SET_ERRORS(state, { key, value }) {
      state.errors[key] = value;
    },
    SET_LIST(state, { key, items }) {
      state[key] = items;
    },
    SET_LOADING_CALENDAR_DATA(state, value) {
      state.loadingCalendarData = value;
    },
    SET_SELECTED_DATE(state, date) {
      state.selectedDate = date;
    },
    SET_CURRENT_MONTH(state, month) {
      state.currentMonth = month;
    },
    SET_AVAILABILITY_DIALOG(state, { show, id }) {
      state.availabilityDialog = { show, id };
    },
    SET_NOTIFICATION(state, { show, success, message }) {
      state.notification = { show, success, message };
    },
  },
  getters: {
    loading: (state) => state.loading,
    errors: (state) => state.errors,
    loadingCalendarData: (state) => state.loadingCalendarData,
    notification: (state) => state.notification,
    currentMonth: (state) => state.currentMonth,
    selectedDate: (state) => state.selectedDate,
    availabilityDialog: (state) => state.availabilityDialog,

    localDateOf: () => (isoUtc) => utcToLocalDate(isoUtc),

    availability: (state) => state.availability,
    appointments: (state) => state.appointments,
    experts: (state) => state.experts,
    serviceTypes: (state) => state.serviceTypes,
    services: (state) => state.services,

    // creation_kind/availability_group/split_from — служебные поля бэка, игнорируются.
    openAvailability: (state) => state.availability.filter((item) => item.status === 'open'),

    availabilityByDate: (state, getters) => (localDate) => getters.openAvailability
      .filter((item) => getters.localDateOf(item.starts_at) === localDate),

    availabilityForMonth: (state, getters) => (month) => getters.openAvailability
      .filter((item) => getters.localDateOf(item.starts_at).startsWith(month)),

    // Отклонённая/отменённая бронь не занимает время — не считается "занято" в счётчиках.
    activeAppointments: (state) => state.appointments
      .filter((item) => item.status !== 'declined' && item.status !== 'cancelled_by_guest'),

    appointmentsByDate: (state, getters) => (localDate) => getters.activeAppointments
      .filter((item) => getters.localDateOf(item.scheduled_starts_at) === localDate),

    cancelledAppointmentsByDate: (state, getters) => (localDate) => state.appointments
      .filter((item) => (item.status === 'declined' || item.status === 'cancelled_by_guest')
        && getters.localDateOf(item.scheduled_starts_at) === localDate),

    appointmentsForMonth: (state, getters) => (month) => getters.activeAppointments
      .filter((item) => getters.localDateOf(item.scheduled_starts_at).startsWith(month)),

    monthSummary: (state, getters) => (month) => {
      const monthAppointments = getters.appointmentsForMonth(month);
      return {
        openSlots: getters.availabilityForMonth(month).length,
        appointments: monthAppointments.length,
        pending: monthAppointments.filter((item) => item.status === 'pending').length,
      };
    },

    expertById: (state) => (id) => state.experts.find((expert) => expert.id === id) || null,

    serviceTypeById: (state) => (id) => state.serviceTypes.find((item) => item.id === id) || null,

    serviceById: (state) => (id) => state.services.find((item) => item.id === id) || null,

    servicesByExpert: (state) => (expertId) => state.services.filter((item) => item.expert === expertId),

    availabilityCountByExpert: (state, getters) => (expertId) => getters.openAvailability
      .filter((item) => item.expert === expertId).length,
  },
};
